import { i18n } from '@/i18n';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { storesApi } from '@/api/stores.api';
import type { SelectOption } from '@/components/ui/Select';
import type { StoreResponse } from '@/types/store';
import { formatStoreLabel } from '@/utils/entityLabels';
import { useDebounce } from '@/hooks/useDebounce';
import { showApiError } from '@/utils/apiError';

function storeOption(s: StoreResponse): SelectOption {
  return { value: String(s.id), label: formatStoreLabel(s) };
}

function mergeSelectedOption(options: SelectOption[], selected?: SelectOption | null): SelectOption[] {
  if (!selected) return options;
  if (options.some((opt) => opt.value === selected.value)) return options;
  return [selected, ...options];
}

type Params = { value?: string; pageSize?: number; activeOnly?: boolean };

export function useStoreSelectSearch({ value = '', pageSize = 50, activeOnly = true }: Params = {}) {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [stores, setStores] = useState<StoreResponse[]>([]);
  const [selected, setSelected] = useState<StoreResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);

  const load = useCallback(
    async (search: string) => {
      setSearching(true);
      try {
        const res = await storesApi.list({
          search: search || undefined,
          page: 0,
          size: pageSize,
          activeOnly,
        });
        setStores(res.content);
      } catch (err) {
        showApiError(err, i18n.t('errors.failedToLoadStores', { ns: 'common' }));
      } finally {
        setSearching(false);
        setLoading(false);
      }
    },
    [activeOnly, pageSize],
  );

  useEffect(() => {
    void load(debouncedSearch);
  }, [debouncedSearch, load]);

  useEffect(() => {
    if (!value) {
      setSelected(null);
      return;
    }
    const id = Number(value);
    if (Number.isNaN(id)) return;
    const fromList = stores.find((s) => s.id === id);
    if (fromList) {
      setSelected(fromList);
      return;
    }
    let cancelled = false;
    storesApi
      .get(id)
      .then((row) => {
        if (!cancelled) setSelected(row);
      })
      .catch(() => {
        if (!cancelled) setSelected(null);
      });
    return () => {
      cancelled = true;
    };
  }, [value, stores]);

  const options = useMemo(() => {
    const base = stores.map(storeOption);
    const sel = selected ? storeOption(selected) : null;
    return mergeSelectedOption(base, sel);
  }, [stores, selected]);

  const byId = useMemo(() => {
    const map = new Map<number, StoreResponse>();
    for (const s of stores) map.set(s.id, s);
    if (selected) map.set(selected.id, selected);
    return map;
  }, [stores, selected]);

  const getStore = useCallback(
    (id: number | string | null | undefined) => {
      if (id == null || id === '') return undefined;
      return byId.get(Number(id));
    },
    [byId],
  );

  return { options, loading, searching, onSearchQuery: setSearchQuery, getStore };
}
